import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Inject,
  CUSTOM_ELEMENTS_SCHEMA,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideHandshake,
  lucideTriangleAlert,
  lucideWrench,
} from '@ng-icons/lucide';
import { LoggerService } from '../../services/logger.service';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { StorefrontService } from '../../services/storefront.service';
import { CompanyService, Company } from '../../services/company.service';
import { AuthService } from '../../services/auth.service';
import { NavbarComponent } from './navbar/navbar.component';
import { FooterComponent } from './footer/footer.component';
import { NotificationsComponent } from '../notifications/notifications.component';
import { InstallPromptComponent } from './install-prompt/install-prompt.component';
import { environment } from '../../../environments/environment';

/**
 * Non-production environments get a banner; production gets none.
 *
 * Background and text colour travel together in one class string: mixing a
 * `[class]` binding with a static `class` attribute on the same element drops
 * one of the two, which left white text on a white bar.
 */
const ENV_BANNERS: Record<string, { label: string; icon: string; theme: string }> = {
  qa: { label: 'DEMO', icon: 'lucideTriangleAlert', theme: 'bg-orange-600 text-white' },
  dev: {
    label: '🛠️ DEVELOPMENT ENVIRONMENT',
    icon: 'lucideWrench',
    theme: 'bg-blue-600 text-white',
  },
};

/** Key for the "hide for today" choice; the value is the ISO date it was made. */
const ENV_BANNER_KEY = 'env-banner-hidden-on';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    NavbarComponent,
    FooterComponent,
    RouterOutlet,
    NotificationsComponent,
    InstallPromptComponent,
    NgIcon,
  ],
  providers: [
    provideIcons({ lucideArrowLeft, lucideHandshake, lucideTriangleAlert, lucideWrench }),
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LayoutComponent implements OnInit, OnDestroy {
  readonly envBanner = ENV_BANNERS[environment.env] ?? null;
  envBannerHidden = false;

  hideNavbar = false;
  hideNotifications = false;
  hideFooter = false;
  hideInstallPrompt = false;
  scrollPosition = 0;
  environment = environment;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private location: Location,
    private storefrontService: StorefrontService,
    private companyService: CompanyService,
    public authService: AuthService,
    @Inject(DOCUMENT) private document: Document,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.envBannerHidden = this.wasEnvBannerHiddenToday();
    this.updateVisibility(this.location.path());
    this.loadVoucherScript(); // Load voucher script once in layout

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updateVisibility(this.location.path());
      });
  }

  /** Hides the environment banner until the next calendar day. */
  hideEnvBannerForToday() {
    this.envBannerHidden = true;
    try {
      localStorage.setItem(ENV_BANNER_KEY, this.todayIso());
    } catch {
      // Private mode or blocked storage: the banner simply returns on reload.
    }
  }

  private wasEnvBannerHiddenToday(): boolean {
    try {
      return localStorage.getItem(ENV_BANNER_KEY) === this.todayIso();
    } catch {
      return false;
    }
  }

  private todayIso(): string {
    return new Date().toISOString().split('T')[0];
  }

  toggleScrollLock(mobileMenuOpen: boolean) {
    if (mobileMenuOpen) {
      this.scrollPosition = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo(0, this.scrollPosition);
    }
  }

  private updateVisibility(url: string) {
    // Check if URL starts with these paths, ignoring parameters
    const hideOnRoutes = [
      '/login',
      '/register',
      '/partner',
      '/forgot-password',
      '/reset-password',
      '/welcome',
      '/verify',
      '/onboarding',
      '/claim',
      '/shop-draft',
    ];
    const isStorefront = this.storefrontService.isStorefront();
    const isGutscheineSubdomain = this.storefrontService.isSubdomain('gutscheine');
    const shouldHide =
      hideOnRoutes.some((route) => url.startsWith(route)) || isStorefront || isGutscheineSubdomain;
    // The design editor needs the height: no alerts, no install prompt, no footer there.
    const isEditor = url.startsWith('/voucher-designs/');
    this.hideNavbar = shouldHide;
    this.hideNotifications = shouldHide || isEditor;
    this.hideInstallPrompt = shouldHide || isEditor;
    this.hideFooter = isEditor;
  }

  loadVoucherScript(): void {
    this.logger.log('📜 [LayoutComponent] Loading voucher script:', environment.voucherScriptUrl);

    // Check if script is already loaded
    const existingScript = this.document.querySelector(
      `script[src="${environment.voucherScriptUrl}"]`,
    );
    if (existingScript) {
      this.logger.log('✅ [LayoutComponent] Voucher script already loaded');
      return;
    }

    // Create and load the script
    const script = this.document.createElement('script');
    script.type = 'module';
    script.src = environment.voucherScriptUrl;
    script.async = true;

    script.onload = () => {
      this.logger.log('✅ [LayoutComponent] Voucher script loaded successfully');
    };

    script.onerror = (error) => {
      this.logger.error('❌ [LayoutComponent] Failed to load voucher script:', error);
    };

    // Append to document head
    this.document.head.appendChild(script);
    this.logger.log('📤 [LayoutComponent] Voucher script added to document head');
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
